import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordModal() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);

  if (!user || user === false || !user.must_change_password || hidden) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated — you're all set");
      setHidden(true);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-6" data-testid="change-password-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <form onSubmit={submit} className="relative w-full max-w-md rounded-xl border border-white/10 bg-[#121212] p-8 shadow-2xl">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 rounded-md bg-[#007AFF]/10 border border-[#007AFF]/30 inline-flex items-center justify-center">
            <KeyRound className="h-4 w-4 text-[#007AFF]" />
          </span>
          <h2 className="font-black tracking-tight text-xl text-white">Set your own password</h2>
        </div>
        <p className="mt-3 text-sm text-neutral-400">
          You're using a temporary password. Choose a new one only you know (min 8 characters).
        </p>
        <div className="mt-5 space-y-3" data-lpignore="true">
          <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-bold">
            1. The temporary password you just signed in with
          </label>
          <Input
            type="password"
            required
            autoComplete="off"
            name="vismarks-temp-pass"
            placeholder="Temporary password (from your admin)"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            data-testid="change-password-current"
            className="bg-[#0A0A0A] border-[#27272A]"
          />
          <label className="block text-[11px] uppercase tracking-widest text-neutral-500 font-bold pt-1">
            2. Your new private password
          </label>
          <Input
            type="password"
            required
            minLength={8}
            autoComplete="new-password"
            name="vismarks-new-pass"
            placeholder="New password (min 8 chars)"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            data-testid="change-password-new"
            className="bg-[#0A0A0A] border-[#27272A]"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="change-password-submit"
          className="mt-5 w-full bg-white text-black hover:bg-neutral-200 rounded-full h-11 font-semibold"
        >
          {busy ? "Saving…" : "Update password"}
        </Button>
      </form>
    </div>
  );
}
